import moment from 'moment/moment'

import { PSApplication } from '../components/app/PSApplication'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { BaseRecord } from './BaseRecord'
import { Image } from './Image'
import { FKType } from './SQLBuilder'
import { User } from './User'

export type CategoryConstructorArgs = ConstructorArgs<() => Category>

export class Category extends BaseRecord {
    readonly recordType = 'category'

    type?: string
    name?: string
    memo?: string
    image?: Image
    sortType?: string

    seqnum?: number // Integer
    isPublic?: boolean
    cancelledDate?: moment.Moment
    lastModifiedDate?: moment.Moment
    listings: string[] = []
    edition?: string
    editionCategory?: string

    constructor(data?: Partial<CategoryConstructorArgs>, params?: { type?: string }) {
        super(data)
        if (!data) {
            this.type = params?.type || 'service'
            this.isPublic = true
            this.listings = []
            this.creator = PSApplication.me()
            this.owner = this.creator.owner
            return
        }

        this.type = data.type ?? undefined
        this.name = data.name ?? undefined
        this.sortType = data.sort_type ?? undefined
        this.seqnum = this.propertyFromIntArg(data.seqnum)
        this.isPublic = data.is_public ?? false
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
        this.image = this.propertyFromImageArg(data.image, data.image_uri)
        this.cancelledDate = this.propertyFromTimestampArg(data.cancelled_date)
        this.listings = data.listings ?? []
        this.memo = data.memo ?? undefined
        this.edition = data.edition ?? undefined
        this.editionCategory = data.edition_category ?? undefined
    }

    clone(overrides?: Partial<CategoryConstructorArgs>): Category {
        return new Category({ ...this.serializeForClone(), ...overrides })
    }

    deserialize(data: any) {
        super.deserialize(data)

        if (data.owner) this.owner = BaseRecord.fromSerializer(data.owner, User)
        if (data.creator) this.creator = BaseRecord.fromSerializer(data.creator, User)
        if (data.image) this.image = BaseRecord.fromSerializer(data.image, Image)
        if (data.cancelledDate) this.cancelledDate = moment(data.cancelledDate)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
    }

    static fk: FKType = {
        users: ['owner', 'creator'],
        images: ['image'],
    }

    jsonObject(): any {
        const dict = super.jsonObject()
        dict['type'] = this.type
        dict['name'] = this.name
        dict['memo'] = this.memo ?? ''
        dict['sort_type'] = this.sortType ?? ''
        if (this.image && this.image.id) dict['image'] = this.image.id
        else if (this.image) dict['image_uri'] = this.image.uri ?? ''
        dict['is_public'] = this.isPublic
        dict['listings'] = this.listings ?? ''
        dict['edition'] = this.edition
        dict['edition_category'] = this.editionCategory
        return dict
    }

    static dataType(): DataType {
        return 'categories' as DataType
    }

    get owner(): User | undefined {
        return this.getForeignRecord('owner', User)
    }
    set owner(owner: User | undefined) {
        this.setForeignRecord('owner', owner)
    }
    get creator(): User | undefined {
        return this.getForeignRecord('creator', User)
    }
    set creator(creator: User | undefined) {
        this.setForeignRecord('creator', creator)
    }
}
